/**
 * Catalog gallery — PUBLIC page (no auth required) — iter27 3-tier model.
 *
 * Banner / badge logic:
 * - anonymous : metadata for everything; demo is "full", rest is "locked"
 * - free      : "X/2 free reads used" banner + upsell
 * - starter   : "X/6 templates selected" badge per card + upsell to Pro
 * - pro       : no banner (unlimited)
 * - admin     : treated as pro
 * - cancelled : "subscription cancelled" banner + reactivate CTA
 */
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight, Lock, Sparkles, Eye, Check, Crown, AlertCircle } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { useLang } from "@/contexts/LanguageContext";
import { api } from "@/lib/api";
import LanguageSwitcher from "@/components/LanguageSwitcher";
import { Overline, PrimaryButton, SecondaryButton } from "@/components/ui-kit";
import usePageMeta from "@/hooks/usePageMeta";

export default function Catalog() {
  const { user, logout, tier, subscription, cancelled } = useAuth();
  const { t, lang } = useLang();
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [list, setList] = useState(null);

  usePageMeta(
    lang === "es"
      ? "Catálogo de planes de negocio — 12 plantillas | Atelier Business"
      : "Business Plan Template Library — 12 industries | Atelier Business",
    lang === "es"
      ? "12 planes de negocio completos por sector: restaurante, café-coworking, SaaS B2B, academia online, food truck y más. Lee 2 gratis."
      : "12 complete business plans by industry: restaurant, coffee-coworking, B2B SaaS, online academy, food truck and more. Read 2 for free."
  );

  useEffect(() => {
    let cancelledReq = false;
    (async () => {
      setLoading(true);
      try {
        const res = await api.get(`/catalog/list`, { params: { language: lang } });
        if (cancelledReq) return;
        setTemplates(res.data.templates);
        setList(res.data);
      } catch (e) {
        // public endpoint — should rarely fail
      } finally {
        if (!cancelledReq) setLoading(false);
      }
    })();
    return () => { cancelledReq = true; };
  }, [lang]);

  function accessBadge(it) {
    if (it.access === "full") {
      if (it.is_public_demo && !user) {
        return { label: t("catalog.demo_badge"), icon: Sparkles, classes: "bg-[#F5EFE6] text-[#B89154] border-[#B89154]/30" };
      }
      if (tier === "starter" && it.is_selected) {
        return { label: lang === "es" ? "Elegida" : "Picked", icon: Check, classes: "bg-[#EFF4FF] text-[#2563EB] border-[#2563EB]/20" };
      }
      return { label: t("catalog.full_badge"), icon: Check, classes: "bg-[#EFF4FF] text-[#2563EB] border-[#2563EB]/20" };
    }
    if (it.access === "available") {
      if (tier === "starter") {
        return { label: lang === "es" ? "Disponible" : "Available", icon: Sparkles, classes: "bg-white text-[#2563EB] border-[#2563EB]/30" };
      }
      return { label: lang === "es" ? "Lectura gratis" : "Free read", icon: Eye, classes: "bg-white text-[#8a6a37] border-[#B89154]/40" };
    }
    if (user) {
      const planLabel = tier === "starter"
        ? (lang === "es" ? "Sube a Pro" : "Upgrade Pro")
        : (lang === "es" ? "Desbloquea" : "Unlock");
      return { label: planLabel, icon: Lock, classes: "bg-[#F3F0EA] text-[#8A8886] border-[#E8E6E1]" };
    }
    return { label: t("catalog.locked_badge"), icon: Lock, classes: "bg-[#F3F0EA] text-[#8A8886] border-[#E8E6E1]" };
  }

  const showFreeBanner = user && tier === "free" && !cancelled;
  const showStarterBanner = user && tier === "starter";
  const showProBanner = user && tier === "pro";
  const showCancelledBanner = user && cancelled;

  return (
    <div className="min-h-screen bg-[#F9F8F6]">
      <header className="border-b border-[#E8E6E1] bg-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-12 py-5 flex items-center justify-between">
          <Link to="/" className="text-xl tracking-tight text-[#1A1A1A]"
            style={{ fontFamily: '"Playfair Display", serif', fontWeight: 700 }}
            data-testid="brand-link">
            Atelier <span className="italic font-normal text-[#B89154]">Business</span>
          </Link>
          <div className="flex items-center gap-4">
            <LanguageSwitcher />
            {user ? (
              <>
                <Link to="/" className="text-sm text-[#5C5B5A] hover:text-[#1A1A1A]" data-testid="my-projects-link">
                  {lang === "es" ? "Mis proyectos" : "My projects"}
                </Link>
                <button onClick={logout} className="text-sm text-[#5C5B5A] hover:text-[#1A1A1A]" data-testid="logout-btn">
                  {t("auth.logout")}
                </button>
              </>
            ) : (
              <Link to="/login" className="text-sm text-[#5C5B5A] hover:text-[#1A1A1A]" data-testid="login-link">
                {t("auth.login")} →
              </Link>
            )}
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 lg:px-12 py-12">
        <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-6 mb-12">
          <div>
            <Overline>{t("catalog.eyebrow")}</Overline>
            <h1
              className="mt-3 text-4xl sm:text-5xl lg:text-6xl tracking-tight text-[#1A1A1A] leading-none"
              style={{ fontFamily: '"Playfair Display", serif', fontWeight: 700 }}
              data-testid="catalog-title"
            >
              {t("catalog.title")}
            </h1>
            <p className="mt-4 text-base text-[#5C5B5A] max-w-xl">{t("catalog.subtitle")}</p>
          </div>
          <div className="flex flex-wrap items-center gap-3">
            {user && (
              <Link
                to="/pricing"
                className="inline-flex items-center gap-2 text-sm text-[#1A1A1A] border border-[#E8E6E1] rounded-sm px-4 py-2 bg-white hover:border-[#2563EB]"
                data-testid="see-pricing-link"
              >
                <span className="text-[10px] uppercase tracking-[0.2em] text-[#8A8886]">
                  {t(`subscription.tier_${tier}`)}
                </span>
                <span>·</span>
                <span className="text-[#2563EB]">{t("paywall.see_plans")}</span>
              </Link>
            )}
            {!user && (
              <Link to="/register">
                <PrimaryButton testId="register-cta-btn">
                  <Sparkles className="h-4 w-4" strokeWidth={2} />
                  {lang === "es" ? "Crear cuenta gratis" : "Create free account"}
                </PrimaryButton>
              </Link>
            )}
          </div>
        </div>

        {/* TIER BANNERS */}
        {showCancelledBanner && (
          <div
            className="mb-8 flex flex-col sm:flex-row sm:items-center justify-between gap-3 border border-[#B89154]/40 bg-[#F5EFE6] rounded-sm px-5 py-4"
            data-testid="catalog-cancelled-banner"
          >
            <div className="flex items-start gap-3">
              <AlertCircle className="h-5 w-5 text-[#B89154] mt-0.5 shrink-0" strokeWidth={1.5} />
              <p className="text-sm text-[#1A1A1A]">{t("paywall.cancelled_banner")}</p>
            </div>
            <Link to="/pricing" className="shrink-0">
              <PrimaryButton testId="catalog-reactivate-btn">
                <Sparkles className="h-4 w-4" strokeWidth={1.5} />
                {lang === "es" ? "Reactivar" : "Reactivate"}
              </PrimaryButton>
            </Link>
          </div>
        )}

        {showFreeBanner && list && (
          <div
            className="mb-8 flex flex-col sm:flex-row sm:items-center justify-between gap-3 border border-[#B89154]/30 bg-[#F5EFE6] rounded-sm px-5 py-4"
            data-testid="catalog-quota-banner"
          >
            <p className="text-sm text-[#1A1A1A]">
              {lang === "es"
                ? `Lecturas gratis usadas: ${list.free_reads_used} de ${list.free_reads_limit}. Sube a Starter (49,90 €/mes) por 6 plantillas a tu elección o a Pro (99,90 €/mes) por acceso ilimitado.`
                : `Free reads used: ${list.free_reads_used} of ${list.free_reads_limit}. Upgrade to Starter ($49.90/mo) for 6 picks or Pro ($99.90/mo) for unlimited access.`}
            </p>
            <Link to="/pricing" className="shrink-0">
              <PrimaryButton testId="catalog-quota-upgrade-btn">
                <Sparkles className="h-4 w-4" strokeWidth={1.5} />
                {t("paywall.see_plans")}
              </PrimaryButton>
            </Link>
          </div>
        )}

        {showStarterBanner && list && (
          <div
            className="mb-8 flex flex-col sm:flex-row sm:items-center justify-between gap-3 border border-[#2563EB]/30 bg-[#EFF4FF] rounded-sm px-5 py-4"
            data-testid="catalog-starter-banner"
          >
            <p className="text-sm text-[#1A1A1A]">
              {lang === "es"
                ? `Plantillas elegidas: ${list.selected_slots_used} de ${list.selected_slots_total}. Abre cualquier plantilla y añádela a tu cupo cuando quieras.`
                : `Templates picked: ${list.selected_slots_used} of ${list.selected_slots_total}. Open any template and add it to your slots when you like.`}
            </p>
            {list.selected_slots_used >= list.selected_slots_total && (
              <Link to="/pricing" className="shrink-0">
                <PrimaryButton testId="catalog-upgrade-pro-btn">
                  <Crown className="h-4 w-4" strokeWidth={1.5} />
                  {lang === "es" ? "Sube a Pro — ilimitado" : "Upgrade to Pro — unlimited"}
                </PrimaryButton>
              </Link>
            )}
          </div>
        )}

        {showProBanner && (
          <div
            className="mb-8 flex items-center gap-3 border border-[#2563EB]/30 bg-[#EFF4FF] rounded-sm px-5 py-3"
            data-testid="catalog-pro-banner"
          >
            <Crown className="h-4 w-4 text-[#2563EB]" strokeWidth={1.5} />
            <p className="text-sm text-[#1A1A1A]">
              {lang === "es"
                ? "Plan Pro · acceso ilimitado a las 12 plantillas y hasta 10 business plans propios al mes."
                : "Pro plan · unlimited access to all 12 templates and up to 10 own business plans per month."}
            </p>
          </div>
        )}

        {loading ? (
          <div className="text-[#8A8886] text-sm tracking-[0.2em] uppercase">{t("common.loading")}</div>
        ) : (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {templates.map((it) => {
              const badge = accessBadge(it);
              const BadgeIcon = badge.icon;
              return (
                <Link
                  key={it.slug}
                  to={`/catalog/${it.slug}`}
                  className="group bg-white border border-[#E8E6E1] rounded-sm overflow-hidden hover:shadow-sm hover:-translate-y-[1px] transition-all flex flex-col"
                  data-testid={`catalog-card-${it.slug}`}
                >
                  <div
                    className="aspect-[5/3] relative overflow-hidden flex items-center justify-center"
                    style={{ backgroundColor: it.accent_color || "#F5EFE6" }}
                  >
                    {it.cover_image_url ? (
                      <img
                        src={it.cover_image_url}
                        alt={it.name}
                        loading="lazy"
                        className="w-full h-full object-cover opacity-90 group-hover:opacity-100 group-hover:scale-[1.02] transition-all duration-500"
                      />
                    ) : null}
                    <div className="absolute top-3 left-3 text-4xl drop-shadow-md" aria-hidden>
                      {it.hero_emoji}
                    </div>
                    <span className={`absolute top-3 right-3 inline-flex items-center gap-1 text-[10px] uppercase tracking-[0.16em] px-2 py-1 rounded-sm border ${badge.classes}`}>
                      <BadgeIcon className="h-3 w-3" strokeWidth={1.5} />
                      {badge.label}
                    </span>
                  </div>
                  <div className="p-6 flex flex-col flex-1">
                    <Overline>{it.industry}</Overline>
                    <h3
                      className="mt-2 text-2xl tracking-tight text-[#1A1A1A] leading-tight"
                      style={{ fontFamily: '"Playfair Display", serif', fontWeight: 600 }}
                    >
                      {it.name}
                    </h3>
                    <p className="mt-2 text-sm text-[#5C5B5A] line-clamp-3 flex-1">
                      {it.tagline || it.description}
                    </p>
                    <div className="mt-4 flex flex-wrap gap-2">
                      {(it.tags || []).slice(0, 3).map((tag) => (
                        <span
                          key={tag}
                          className="text-[10px] uppercase tracking-[0.15em] text-[#8A8886] border border-[#E8E6E1] rounded-sm px-2 py-1 bg-[#FBFAF7]"
                        >
                          {tag}
                        </span>
                      ))}
                    </div>
                    <div className="mt-5 pt-4 border-t border-[#E8E6E1] flex items-center justify-between text-xs">
                      <span className="text-[#8A8886]">
                        <span className="uppercase tracking-[0.15em]">{t("catalog.investment_label")}: </span>
                        <span className="text-[#1A1A1A] font-medium" style={{ fontFamily: '"IBM Plex Mono", monospace' }}>
                          {it.investment_range || "—"}
                        </span>
                      </span>
                      <span className="inline-flex items-center gap-1 text-[#2563EB] group-hover:gap-2 transition-all">
                        {t("catalog.open_button")}
                        <ArrowUpRight className="h-4 w-4" strokeWidth={1.5} />
                      </span>
                    </div>
                  </div>
                </Link>
              );
            })}
          </div>
        )}

        {!user && (
          <div
            className="mt-16 border border-[#2563EB]/20 bg-[#EFF4FF]/60 rounded-sm p-8 lg:p-10 flex flex-col lg:flex-row items-start lg:items-center justify-between gap-6"
            data-testid="catalog-cta-banner"
          >
            <div>
              <Overline>{lang === "es" ? "Para tu negocio" : "For your business"}</Overline>
              <h3
                className="mt-2 text-2xl sm:text-3xl text-[#1A1A1A]"
                style={{ fontFamily: '"Playfair Display", serif', fontWeight: 600 }}
              >
                {lang === "es"
                  ? "Inspírate y crea tu propio plan con IA"
                  : "Get inspired and build your own plan with AI"}
              </h3>
              <p className="mt-2 text-sm text-[#5C5B5A] max-w-2xl">
                {lang === "es"
                  ? "Crea una cuenta gratis y lee 2 plantillas completas + tu propio plan editable. Sube a Starter (49,90 €) por 6 plantillas a elegir o a Pro (99,90 €) por acceso ilimitado."
                  : "Create a free account and read any 2 templates in full + 1 editable plan. Upgrade to Starter ($49.90) for 6 picks or Pro ($99.90) for unlimited access."}
              </p>
            </div>
            <div className="flex gap-3 shrink-0">
              <Link to="/register">
                <PrimaryButton testId="catalog-cta-register-btn">
                  {lang === "es" ? "Crear cuenta" : "Create account"}
                </PrimaryButton>
              </Link>
              <Link to="/login">
                <SecondaryButton testId="catalog-cta-login-btn">
                  {t("auth.login")}
                </SecondaryButton>
              </Link>
            </div>
          </div>
        )}
      </main>
    </div>
  );
}
